import React from 'react';
import { Immutable } from 'nuclear-js';
import icons from 'glyphicons';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import Analytics from 'utils/analytics';
import CommonValues from 'resources/common-values';
import CommonStrings from 'resources/common-strings';

export default CreateReactClass({
    displayName: 'GradeDropdown',

    propTypes: {
        grades: PropTypes.instanceOf(Immutable.List),
        selectedGrades: PropTypes.instanceOf(Immutable.Set).isRequired,
        onDropdownHide: PropTypes.func.isRequired,
        disabled: PropTypes.bool.isRequired,
    },

    getInitialState() {
        return {
            gradeDropdownOpen: false,
            currentSelectedGrades: null,
        };
    },

    UNSAFE_componentWillMount() {
        this.setState({
            currentSelectedGrades: this.props.selectedGrades,
        });
    },

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.selectedGrades !== this.props.selectedGrades) {
            this.setState({
                currentSelectedGrades: nextProps.selectedGrades,
            });
        }

        // this looks a little weird, but when the control is disabled, reset
        //      the selected grades to all grades
        if (nextProps.disabled && !this.props.disabled) {
            nextProps.onDropdownHide(this.getAllGrades(nextProps.grades));
        }
    },

    allGradesCurrentlySelectedYn() {
        return this.props.grades.size === this.state.currentSelectedGrades.size;
    },

    /**
     * Handles the escape key-up event which causes the grades dropdown list to close (only if it is currently open)
     * in a similar manner to a combo-box dropdown, which this control acts like even though it's just a button and
     * associated list.
     *
     * @param event the key-up event from which the key which was pressed can be found.
     */
    handleEscKeyUp(event) {
        const escKeyCode = 27;
        //If the key pressed was the escape key and the grade list is currently open - toggle it
        if (this.state.gradeDropdownOpen && event.keyCode === escKeyCode) {
            this.toggleGradeList();
        }
    },

    toggleGradeList() {
        this.setState({
            gradeDropdownOpen: !this.state.gradeDropdownOpen,
        });
        // handle the bizarro case where user unchecked all grades and didn't select any
        //    other grades--in that case, treat as all grades, per requirement
        const grades = this.state.currentSelectedGrades.isEmpty()
            ? this.getAllGrades()
            : this.state.currentSelectedGrades;
        this.props.onDropdownHide(grades);
    },

    onGradeClick(event, grade) {
        Analytics.recordButtonClick(CommonValues.SectionIDs.instructionalAreas, 'change-grade');
        // if all grades were selected, but now a single one is, remove all except for the newly selected grade
        if (this.allGradesCurrentlySelectedYn()) {
            this.setState({
                currentSelectedGrades: Immutable.Set.of(grade),
            });
        } else if (!this.state.currentSelectedGrades.contains(grade)) {
            this.setState({
                currentSelectedGrades: this.state.currentSelectedGrades.add(grade),
            });
        } else {
            this.setState({
                currentSelectedGrades: this.state.currentSelectedGrades.delete(grade),
            });
        }
    },

    checkAll(event) {
        Analytics.recordButtonClick(CommonValues.SectionIDs.instructionalAreas, 'change-grade');
        if (!this.allGradesCurrentlySelectedYn()) {
            this.setState({
                currentSelectedGrades: this.getAllGrades(), // select all
            });
        } else {
            this.setState({
                currentSelectedGrades: Immutable.Set(), // this is the screwy case of unselecting all
            });
        }
        event.stopPropagation();
    },

    getAllGrades(grades = this.props.grades) {
        return grades.map((g) => g.get('name')).toSet();
    },

    isCheckboxChecked(value) {
        if (this.allGradesCurrentlySelectedYn()) return false;

        return this.state.currentSelectedGrades.includes(value);
    },

    getButtonLabel() {
        // show all grades if they're all selected or in the case where all grades was unchecked
        //   but no other grade was selected
        if (this.allGradesCurrentlySelectedYn() || this.state.currentSelectedGrades.isEmpty()) {
            const label = `All${CommonStrings.nbsp}Grades `;
            return label;
        } else {
            const grades = this.state.currentSelectedGrades.sortBy((g) => parseInt(g));
            // The following changes any space within a grade (i.e. 'Grade 5') to be a non-breaking space for the purposes of printing.
            // It's convoluted because passing '&nbsp;' through jsx converts it to &amp;nbsp;
            return grades.map((g) => g.replace(/ /g, CommonStrings.nbsp)).join(', ');
        }
    },

    render() {
        const gradeButtonClasses = `grade-list-button ${this.state.gradeDropdownOpen ? 'open' : ''}`;
        const button = (
            <button
                value="All Grades"
                onClick={this.toggleGradeList}
                onKeyUp={this.handleEscKeyUp}
                key="grade-list-button"
                disabled={this.props.disabled}
                className={gradeButtonClasses}
            >
                <span className="grade-list-summary">{this.getButtonLabel()}</span>
                <span className="caret">{icons.arrowTriD}</span>
            </button>
        );

        const gradeListClasses = `grade-list ${this.state.gradeDropdownOpen ? 'open' : ''}`;
        const gradeList = [
            <li key="all-grades" onClick={this.checkAll}>
                <input type="checkbox" checked={this.allGradesCurrentlySelectedYn()} readOnly={true} />
                <label>All Grades</label>
                <hr />
            </li>,
        ];
        if (this.props.grades) {
            this.props.grades.forEach((g) => {
                const grade = g.get('name');
                gradeList.push(
                    <li key={grade} onClick={(event) => this.onGradeClick(event, grade)}>
                        <input type="checkbox" checked={this.isCheckboxChecked(grade)} readOnly={true} />
                        <label>{grade}</label>
                    </li>
                );
            });
        }

        const overlayClasses = `overlay${this.state.gradeDropdownOpen ? ' open' : ''}`;
        const overlay = (
            <div key={`overlay-grade-dropdown`} className={overlayClasses} onClick={this.toggleGradeList} />
        );

        // Setting the tabindex to 0 allows the gradeListClasses ul to be given focus - allowing it to react to
        // key up events.  This was added so that users can press escape to exit the list which otherwise looks
        // and acts like a combo-box.
        return (
            <div className="grade-dropdown">
                {button}
                <ul className={gradeListClasses} key="grade-list" onKeyUp={this.handleEscKeyUp} tabIndex="0">
                    {gradeList}
                </ul>
                {overlay}
            </div>
        );
    },
});
